import { useEffect, useRef, useState, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { latLngToCell, cellToBoundary, gridDisk } from 'h3-js'
import { Crosshair, Locate, Loader2, ChevronUp, ChevronDown, Map, Shield, X, Building2, ArrowLeftCircle, Plus, MapPinOff, Skull, Navigation, Bug, LayoutList } from 'lucide-react'
import { useGameStore } from '../../store/gameStore'
import { useAuthStore } from '../../store/authStore'
import { api } from '../../services/api'
import { sendLocationUpdate } from '../../services/socket'
import BattlePanel from './BattlePanel'
import BattleResultOverlay from './BattleResultOverlay'
import { UnitSprite, StructureSprite } from './sprites'
import type { Zone, UnitType } from '@territory-nexus/shared'
import { RESOURCE_CONFIG, RESOURCE_ORDER, UNIT_META, STRUCTURE_META, getZoneBiome, getZoneName, getZoneModifiers, getZoneRates } from '@territory-nexus/shared'

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || ''

const H3_RESOLUTION = 9 // ~174m hexagons
const GRID_RING_SIZE = 3 // How many rings of hexes to show around player

const GPS_UPDATE_INTERVAL = parseInt(import.meta.env.VITE_GPS_UPDATE_INTERVAL || '5000', 10)
const MIN_MOVE_METERS = 10 // Only fetch zones if moved more than this

const MOCK_GPS = import.meta.env.VITE_MOCK_GPS === 'true'
const MOCK_LAT = parseFloat(import.meta.env.VITE_MOCK_LAT || '39.4119')
const MOCK_LNG = parseFloat(import.meta.env.VITE_MOCK_LNG || '-94.9022')

// Haversine distance in meters between two [lng, lat] points
function distanceMeters(a: [number, number], b: [number, number]): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const R = 6371000
  const dLat = toRad(b[1] - a[1])
  const dLng = toRad(b[0] - a[0])
  const sinLat = Math.sin(dLat / 2)
  const sinLng = Math.sin(dLng / 2)
  const h = sinLat * sinLat + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * sinLng * sinLng
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

export default function GameMap() {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const [mapReady, setMapReady] = useState(false)
  const userMarker = useRef<mapboxgl.Marker | null>(null)
  const lastFetchLocation = useRef<[number, number] | null>(null)
  const lastFetchTime = useRef<number>(0)
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null)
  const [currentHexId, setCurrentHexId] = useState<string | null>(null)
  const [isTracking, setIsTracking] = useState(true)
  const [isClaiming, setIsClaiming] = useState(false)
  const [claimError, setClaimError] = useState<string | null>(null)
  const [selectedHexId, setSelectedHexId] = useState<string | null>(null)
  const [showBattle, setShowBattle] = useState(false)
  const [battleZoneId, setBattleZoneId] = useState<string | null>(null)
  const [gpsError, setGpsError] = useState<string | null>(null)
  const [claimFlash, setClaimFlash] = useState(false)
  const [showZoneNav, setShowZoneNav] = useState(false)
  const [showZoneDetails, setShowZoneDetails] = useState(false)
  const [viewedHexId, setViewedHexId] = useState<string | null>(null)

  // Dev mode teleport
  const [devTeleport, setDevTeleport] = useState(import.meta.env.DEV)

  const { player } = useAuthStore()
  const {
    nearbyZones,
    playerZones,
    playerStats,
    units,
    fetchNearbyZones,
    fetchPlayerZones,
    fetchUnits,
    fetchResources,
    claimZone,
    setCurrentZone,
    deployUnit,
    recallUnit,
  } = useGameStore()
  
  // Quick deploy/recall state
  const [showQuickDeploy, setShowQuickDeploy] = useState(false)
  const [isDeploying, setIsDeploying] = useState(false)

  // PvE attack state
  const [showPveSelect, setShowPveSelect] = useState(false)
  const [pveSelectedUnits, setPveSelectedUnits] = useState<string[]>([])
  const [isPveAttacking, setIsPveAttacking] = useState(false)
  const [pveSpawnInfo, setPveSpawnInfo] = useState<{
    difficulty: number
    aiUnits: { unitType: UnitType; level: number; currentHp: number }[]
  } | null>(null)
  const [pveResult, setPveResult] = useState<{
    battleId: string
    victory: boolean
    turns: number
    highlights: { turn: number; type: string; description: string; unitId: string; targetId?: string; damage?: number }[]
    playerUnits: { id: string; unitType: UnitType; currentHp: number; maxHp: number }[]
    aiUnits: { id: string; unitType: UnitType; currentHp: number; maxHp: number }[]
    rewards: { resources: Record<string, number>; modDropped: boolean } | null
    modDrop?: { rarity: string; modType: string }
  } | null>(null)

  // Dev mode: spawn corrupted zones
  const [isDevSpawning, setIsDevSpawning] = useState(false)

  const devSpawnCorrupted = async () => {
    if (!userLocation) return
    setIsDevSpawning(true)
    try {
      // Spawn corrupted zones on neighboring hexes
      const hexId = latLngToCell(userLocation[1], userLocation[0], 9)
      const ring = gridDisk(hexId, 2)
      let spawned = 0
      for (const h of ring) {
        if (h === hexId) continue // skip player's hex
        if (spawned >= 3) break
        const boundary = cellToBoundary(h, true) as [number, number][]
        const center = boundary.reduce(
          (acc, c) => [acc[0] + c[0] / boundary.length, acc[1] + c[1] / boundary.length],
          [0, 0] as [number, number]
        )
        try {
          await api.post('/ai/dev/spawn-at', {
            lat: center[1],
            lng: center[0],
            difficulty: Math.floor(Math.random() * 5) + 1,
          })
          spawned++
        } catch {
          // skip if already corrupted or owned
        }
      }
      // Refresh zones
      if (spawned > 0) {
        fetchNearbyZones(userLocation[1], userLocation[0])
      }
    } catch (err) {
      console.error('Dev spawn error:', err)
    } finally {
      setIsDevSpawning(false)
    }
  }

  // Track corrupted zone markers
  const corruptedMarkersRef = useRef<mapboxgl.Marker[]>([])
  // Ref for hex click handler to avoid stale closures
  const setSelectedHexIdRef = useRef(setSelectedHexId)
  setSelectedHexIdRef.current = setSelectedHexId

  // Generate hex boundary for display
  const getHexBoundary = useCallback((hexId: string) => {
    // cellToBoundary with true returns GeoJSON format [lng, lat] - use directly
    const boundary = cellToBoundary(hexId, true)
    return boundary as [number, number][]
  }, [])


  // Update user location and current hex
  const updateUserLocation = useCallback(
    (coords: [number, number]) => {
      setUserLocation(coords)

      // Calculate current H3 hex
      const hexId = latLngToCell(coords[1], coords[0], H3_RESOLUTION)
      setCurrentHexId(hexId)

      // Update marker
      if (userMarker.current) {
        userMarker.current.setLngLat(coords)
      } else if (map.current) {
        userMarker.current = new mapboxgl.Marker({ color: '#00d4ff' })
          .setLngLat(coords)
          .addTo(map.current)
      }

      // Find matching zone in nearby zones
      const matchingZone = nearbyZones.find((z) => z.hexId === hexId)
      setCurrentZone(matchingZone || null)
    },
    [nearbyZones, setCurrentZone]
  )

  // Sync dev teleport state to DOM for map click handler
  useEffect(() => {
    if (import.meta.env.DEV) {
      document.body.dataset.devTeleport = devTeleport ? 'true' : 'false'
    }
  }, [devTeleport])

  // Stable refs for functions used inside map load callback (avoids stale closures)
  const updateUserLocationRef = useRef(updateUserLocation)
  const fetchNearbyZonesRef = useRef(fetchNearbyZones)
  const fetchPlayerZonesRef = useRef(fetchPlayerZones)
  useEffect(() => { updateUserLocationRef.current = updateUserLocation }, [updateUserLocation])
  useEffect(() => { fetchNearbyZonesRef.current = fetchNearbyZones }, [fetchNearbyZones])
  useEffect(() => { fetchPlayerZonesRef.current = fetchPlayerZones }, [fetchPlayerZones])

  // Initialize map — defer until container has layout dimensions
  useEffect(() => {
    const container = mapContainer.current
    if (!container) return

    // Clean up any leftover map from StrictMode re-mount
    if (map.current) {
      map.current.remove()
      map.current = null
    }
    userMarker.current = null

    let cancelled = false
    let rafId: number
    let mapInstance: mapboxgl.Map | null = null
    let resizeTimers: ReturnType<typeof setTimeout>[] = []

    const initMap = () => {
      if (cancelled) return
      // Mapbox requires non-zero container dimensions; wait until layout is done
      const { offsetWidth, offsetHeight } = container
      if (offsetWidth === 0 || offsetHeight === 0) {
        rafId = requestAnimationFrame(initMap)
        return
      }

      const initialCenter: [number, number] = MOCK_GPS ? [MOCK_LNG, MOCK_LAT] : [-74.5, 40]

      mapInstance = new mapboxgl.Map({
        container,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: initialCenter,
        zoom: MOCK_GPS ? 16 : 15,
        pitch: 45,
        bearing: 0,
        attributionControl: false,
        failIfMajorPerformanceCaveat: false,
      })
      map.current = mapInstance

      // Minimal controls - hide on mobile
      mapInstance.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left')

      // Embedded browsers / VSCode webview may finalize layout after map init.
      // Fire staggered resizes to catch late container dimension changes.
      resizeTimers = [100, 300, 800, 1500].map(ms =>
        setTimeout(() => { if (!cancelled) mapInstance?.resize() }, ms)
      )

      mapInstance.on('load', () => {
        if (cancelled) return
        setMapReady(true)
        mapInstance?.resize()
        const initLocation = (lat: number, lng: number) => {
          setGpsError(null)
          const coords: [number, number] = [lng, lat]
          updateUserLocationRef.current(coords)
          if (!MOCK_GPS) {
            mapInstance?.flyTo({ center: coords, zoom: 16 })
          }
          lastFetchLocation.current = coords
          lastFetchTime.current = Date.now()
          fetchNearbyZonesRef.current(lat, lng)
          sendLocationUpdate(lat, lng)
        }

        if (MOCK_GPS) {
          initLocation(MOCK_LAT, MOCK_LNG)
          return
        }

        if (!navigator.geolocation) {
          setGpsError('Geolocation not supported by this browser. Use a real browser with GPS access.')
          return
        }

        navigator.geolocation.getCurrentPosition(
          (position) => initLocation(position.coords.latitude, position.coords.longitude),
          (error) => {
            console.error('Geolocation error:', error)
            if (error.code === error.PERMISSION_DENIED) {
              setGpsError('GPS permission denied. Enable location access in your browser settings.')
            } else if (error.code === error.POSITION_UNAVAILABLE) {
              setGpsError('GPS unavailable. This browser may not support device location.')
            } else {
              setGpsError('GPS timed out. Make sure location services are enabled.')
            }
          },
          { enableHighAccuracy: true, timeout: 10000 }
        )
      })

      // Fetch player's zones on mount
      fetchPlayerZonesRef.current()

      // Dev mode: click to teleport
      if (import.meta.env.DEV) {
        mapInstance.on('click', (e) => {
          // Only teleport if dev teleport is active (check via DOM data attribute)
          if (document.body.dataset.devTeleport !== 'true') return
          const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat]
          updateUserLocationRef.current(coords)
          lastFetchLocation.current = coords
          lastFetchTime.current = Date.now()
          fetchNearbyZonesRef.current(e.lngLat.lat, e.lngLat.lng)
        })
      }
    }

    rafId = requestAnimationFrame(initMap)

    // ResizeObserver: if the container size changes after init, tell Mapbox
    const ro = new ResizeObserver(() => {
      mapInstance?.resize()
    })
    ro.observe(container)

    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      resizeTimers.forEach(clearTimeout)
      ro.disconnect()
      if (mapInstance) {
        mapInstance.remove()
        mapInstance = null
      }
      map.current = null
      userMarker.current = null
    }
  }, [])

  // Watch for location changes when tracking
  useEffect(() => {
    if (!isTracking || MOCK_GPS) return
    if (!navigator.geolocation) return

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const coords: [number, number] = [position.coords.longitude, position.coords.latitude]

        // Always update the visual marker position (cheap)
        updateUserLocation(coords)
        if (map.current) {
          map.current.setCenter(coords)
        }

        // Always send location to server for proximity enforcement
        sendLocationUpdate(position.coords.latitude, position.coords.longitude)

        // Only fetch zones if moved enough AND enough time has passed
        const now = Date.now()
        const movedEnough = !lastFetchLocation.current || distanceMeters(lastFetchLocation.current, coords) > MIN_MOVE_METERS
        const timeElapsed = now - lastFetchTime.current > GPS_UPDATE_INTERVAL

        if (movedEnough || timeElapsed) {
          lastFetchLocation.current = coords
          lastFetchTime.current = now
          fetchNearbyZones(position.coords.latitude, position.coords.longitude)
        }
      },
      (error) => {
        // Code 3 = timeout — common on desktop browsers without GPS hardware, not worth logging
        if (error.code !== error.TIMEOUT) {
          console.error('Watch position error:', error)
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: GPS_UPDATE_INTERVAL }
    )

    return () => {
      navigator.geolocation.clearWatch(watchId)
    }
  }, [isTracking, updateUserLocation, fetchNearbyZones])

  // Generate and render nearby hexes grid on map
  useEffect(() => {
    if (!map.current || !mapReady || !currentHexId) return

    const sourceId = 'nearby-zones'
    const fillLayerId = 'nearby-zones-fill'
    const outlineLayerId = 'nearby-zones-outline'

    // Generate grid around player location
    const localHexIds = gridDisk(currentHexId, GRID_RING_SIZE)
    // Also generate grid around viewed location (when navigating to remote zones)
    const remoteHexIds = viewedHexId && viewedHexId !== currentHexId
      ? gridDisk(viewedHexId, GRID_RING_SIZE)
      : []
    // Merge and deduplicate
    const nearbyHexIds = [...new Set([...localHexIds, ...remoteHexIds])]

    // Build GeoJSON features for ALL nearby hexes
    const features: GeoJSON.Feature[] = nearbyHexIds.map((hexId) => {
      const boundary = getHexBoundary(hexId)
      
      // Check if this hex exists in database (claimed zone)
      const existingZone = nearbyZones.find((z) => z.hexId === hexId)
      const isOwned = existingZone?.ownerId === player?.id
      const isClaimed = !!existingZone?.ownerId
      const isCurrent = hexId === currentHexId
      const isCorrupted = existingZone?.zoneType === 'corrupted'
      const isSelected = hexId === selectedHexId && hexId !== currentHexId
      const biome = getZoneBiome(hexId)

      return {
        type: 'Feature',
        properties: {
          hexId,
          isOwned,
          isClaimed,
          isCurrent,
          isCorrupted,
          isSelected,
          integrity: existingZone?.integrity ?? 100,
          biomeColor: biome.color,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [boundary],
        },
      }
    })

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    }

    const source = map.current.getSource(sourceId) as mapboxgl.GeoJSONSource
    if (source) {
      source.setData(geojson)
    } else {
      map.current.addSource(sourceId, {
        type: 'geojson',
        data: geojson,
      })

      // Add fill layer - more visible
      map.current.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': [
            'case',
            ['get', 'isCorrupted'], '#a855f7',
            ['get', 'isCurrent'], '#06b6d4',
            ['get', 'isOwned'], '#10b981',
            ['get', 'isClaimed'], '#f59e0b',
            ['get', 'isSelected'], '#94a3b8',
            '#1e293b' // dark base for unclaimed
          ],
          'fill-opacity': [
            'case',
            ['get', 'isCorrupted'], 0.5,
            ['get', 'isCurrent'], 0.5,
            ['get', 'isOwned'], 0.4,
            ['get', 'isClaimed'], 0.35,
            ['get', 'isSelected'], 0.35,
            0.25
          ],
        },
      })

      // Add outline layer - thicker for visibility
      map.current.addLayer({
        id: outlineLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': [
            'case',
            ['get', 'isCorrupted'], '#c084fc',
            ['get', 'isCurrent'], '#22d3ee',
            ['get', 'isOwned'], '#34d399',
            ['get', 'isClaimed'], '#fbbf24',
            ['get', 'isSelected'], '#cbd5e1',
            ['get', 'biomeColor'] // subtle biome tint on outline
          ],
          'line-width': [
            'case',
            ['get', 'isCorrupted'], 2.5,
            ['get', 'isCurrent'], 3,
            ['get', 'isSelected'], 2.5,
            1.5
          ],
          'line-opacity': [
            'case',
            ['get', 'isCorrupted'], 1,
            ['get', 'isCurrent'], 1,
            ['get', 'isOwned'], 1,
            ['get', 'isClaimed'], 1,
            ['get', 'isSelected'], 1,
            0.4 // muted biome outline for unclaimed
          ],
        },
      })

      // Inner glow layer for owned zones
      map.current.addLayer({
        id: 'owned-zone-glow',
        type: 'line',
        source: sourceId,
        filter: ['get', 'isOwned'],
        paint: {
          'line-color': '#34d399',
          'line-width': 6,
          'line-blur': 4,
          'line-opacity': 0.3,
        },
      })

      // Inner glow layer for corrupted zones
      map.current.addLayer({
        id: 'corrupted-zone-glow',
        type: 'line',
        source: sourceId,
        filter: ['get', 'isCorrupted'],
        paint: {
          'line-color': '#a855f7',
          'line-width': 8,
          'line-blur': 5,
          'line-opacity': 0.4,
        },
      })

      // Click handler: tap a hex to select it
      map.current.on('click', fillLayerId, (e) => {
        const feature = e.features?.[0]
        if (feature?.properties?.hexId) {
          setSelectedHexIdRef.current(feature.properties.hexId)
        }
      })

      // Pointer cursor on hover over hexes
      map.current.on('mouseenter', fillLayerId, () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer'
      })
      map.current.on('mouseleave', fillLayerId, () => {
        if (map.current) map.current.getCanvas().style.cursor = ''
      })
    }
  }, [mapReady, currentHexId, viewedHexId, nearbyZones, player?.id, getHexBoundary, selectedHexId])

  // Animate corrupted zone opacity pulse
  useEffect(() => {
    if (!map.current || !currentHexId) return
    let frame = 0
    let animId: number
    const animate = () => {
      frame++
      const pulseValue = 0.35 + Math.sin(frame * 0.03) * 0.2
      try {
        if (map.current?.getLayer('nearby-zones-fill')) {
          map.current.setPaintProperty('nearby-zones-fill', 'fill-opacity', [
            'case',
            ['get', 'isCorrupted'], pulseValue,
            ['get', 'isCurrent'], 0.5,
            ['get', 'isOwned'], 0.4,
            ['get', 'isClaimed'], 0.35,
            0.25
          ])
        }
        if (map.current?.getLayer('corrupted-zone-glow')) {
          map.current.setPaintProperty('corrupted-zone-glow', 'line-opacity', Math.max(0, 0.2 + Math.sin(frame * 0.04) * 0.25))
        }
      } catch { /* layer might not exist yet */ }
      animId = requestAnimationFrame(animate)
    }
    animId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animId)
  }, [currentHexId])

  // Fetch units on mount
  useEffect(() => {
    fetchUnits()
  }, [fetchUnits])

  // Render skull markers on corrupted zones
  useEffect(() => {
    if (!map.current) return

    corruptedMarkersRef.current.forEach(marker => marker.remove())
    corruptedMarkersRef.current = []

    const corruptedZones = nearbyZones.filter(z => z.zoneType === 'corrupted')

    corruptedZones.forEach((zone) => {
      let boundary: [number, number][]
      try {
        boundary = getHexBoundary(zone.hexId)
        if (!boundary || boundary.length === 0) return
      } catch {
        return
      }
      const center: [number, number] = boundary.reduce(
        (acc, coord) => [acc[0] + coord[0] / boundary.length, acc[1] + coord[1] / boundary.length],
        [0, 0] as [number, number]
      )

      const el = document.createElement('div')
      el.style.cssText = `
        width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
        pointer-events: none; position: relative;
      `
      el.innerHTML = `
        <div style="position:absolute;inset:-4px;border-radius:50%;background:radial-gradient(circle,rgba(168,85,247,0.3),transparent 70%);animation:pulse 2s ease-in-out infinite;"></div>
        <div style="position:absolute;inset:0;border:1.5px solid rgba(168,85,247,0.5);border-radius:50%;animation:pulse 2s ease-in-out infinite;"></div>
        <span style="font-size:18px;filter:drop-shadow(0 0 6px rgba(168,85,247,0.8)) drop-shadow(0 0 2px rgba(239,68,68,0.5));z-index:1;animation:pulse 2s ease-in-out infinite;">☠</span>
      `

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(center)
        .addTo(map.current!)
      corruptedMarkersRef.current.push(marker)
    })

    return () => {
      corruptedMarkersRef.current.forEach(marker => marker.remove())
      corruptedMarkersRef.current = []
    }
  }, [nearbyZones, getHexBoundary])

  const centerOnUser = () => {
    if (userLocation && map.current) {
      map.current.flyTo({ center: userLocation, zoom: 16 })
      setViewedHexId(null)
      setSelectedHexId(null)
      // Re-fetch zones around player location
      fetchNearbyZones(userLocation[1], userLocation[0])
    }
  }

  // Fly to a specific hex zone and render grid there
  const flyToZone = (hexId: string) => {
    if (!map.current) return
    setIsTracking(false)
    const boundary = getHexBoundary(hexId)
    const center: [number, number] = boundary.reduce(
      (acc, coord) => [acc[0] + coord[0] / boundary.length, acc[1] + coord[1] / boundary.length],
      [0, 0]
    )
    map.current.flyTo({ center, zoom: 17 })
    // Set viewed hex so grid renders at destination
    setViewedHexId(hexId)
    // Fetch zones around the destination
    fetchNearbyZones(center[1], center[0])
  }

  const toggleTracking = () => {
    setIsTracking(!isTracking)
  }

  // Open battle panel for a zone
  const handleAttackZone = (hexId: string) => {
    const zone = nearbyZones.find((z) => z.hexId === hexId)
    if (zone?.id) {
      setBattleZoneId(zone.id)
      setShowBattle(true)
    }
  }

  // Fetch spawn info when opening PvE panel for a corrupted zone
  const fetchSpawnInfo = async (zoneId: string) => {
    try {
      const res = await api.get<{ success: boolean; data: { difficulty: number; aiUnits: { unitType: UnitType; level: number; currentHp: number; stats: { hp: number } }[] } }>(`/ai/spawns/zone/${zoneId}`)
      if (res.data.success) {
        setPveSpawnInfo({
          difficulty: res.data.data.difficulty,
          aiUnits: res.data.data.aiUnits.map(u => ({ unitType: u.unitType, level: u.level, currentHp: u.currentHp })),
        })
      }
    } catch {
      setPveSpawnInfo(null)
    }
  }

  const handlePveAttack = async (zoneId: string) => {
    if (pveSelectedUnits.length === 0) return
    setIsPveAttacking(true)
    try {
      const res = await api.post<{ success: boolean; data: typeof pveResult }>(`/ai/attack/${zoneId}`, {
        unitIds: pveSelectedUnits,
      })
      if (res.data.success) {
        setPveResult(res.data.data)
        setShowPveSelect(false)
        setPveSelectedUnits([])
        setPveSpawnInfo(null)
        // Refresh data
        fetchUnits()
        fetchResources()
        fetchNearbyZones(userLocation![1], userLocation![0])
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setClaimError(error.response?.data?.error || 'PvE attack failed')
      setTimeout(() => setClaimError(null), 3000)
    } finally {
      setIsPveAttacking(false)
    }
  }

  const togglePveUnit = (unitId: string) => {
    setPveSelectedUnits(prev =>
      prev.includes(unitId)
        ? prev.filter(id => id !== unitId)
        : prev.length < 3
          ? [...prev, unitId]
          : prev
    )
  }

  const closeBattle = () => {
    setShowBattle(false)
    setBattleZoneId(null)
    // Refresh data after battle
    fetchPlayerZones()
    fetchUnits()
  }

  const activeHexId = selectedHexId || currentHexId

  // Quick deploy an idle unit to the active zone
  const handleQuickDeploy = async (unitId: string) => {
    const zone = playerZones.find(z => z.hexId === activeHexId)
    if (!zone) return
    
    setIsDeploying(true)
    try {
      await deployUnit(unitId, zone.id)
      setShowQuickDeploy(false)
    } catch (err: unknown) {
      setClaimError(err instanceof Error ? err.message : 'Failed to deploy')
      setTimeout(() => setClaimError(null), 3000)
    } finally {
      setIsDeploying(false)
    }
  }

  // Recall all units from the active zone
  const handleQuickRecall = async () => {
    const zone = playerZones.find(z => z.hexId === activeHexId)
    if (!zone) return
    
    const zoneUnits = units.filter(u => u.zoneId === zone.id && u.status === 'deployed')
    setIsDeploying(true)
    try {
      await Promise.all(zoneUnits.map(u => recallUnit(u.id)))
    } catch (err: unknown) {
      setClaimError(err instanceof Error ? err.message : 'Failed to recall')
      setTimeout(() => setClaimError(null), 3000)
    } finally {
      setIsDeploying(false)
    }
  }

  // Get idle units for quick deploy
  const idleUnits = units.filter(u => u.status === 'idle')

  const handleClaimZone = async () => {
    if (!currentHexId || !userLocation) return

    setIsClaiming(true)
    setClaimError(null)

    try {
      await claimZone(currentHexId, userLocation[1], userLocation[0])
      // Success flash
      setClaimFlash(true)
      setTimeout(() => setClaimFlash(false), 1500)
    } catch (error: unknown) {
      setClaimError(error instanceof Error ? error.message : 'Failed to claim zone')
      setTimeout(() => setClaimError(null), 3000)
    } finally {
      setIsClaiming(false)
    }
  }

  // Check if player owns a zone adjacent to the given hex
  const hasAdjacentOwnedZone = (hexId: string): boolean => {
    const neighbors = gridDisk(hexId, 1)
    return playerZones.some(z => neighbors.includes(z.hexId))
  }

  // Can the player attack/purge this zone? (standing in it OR owns an adjacent zone)
  const canReachZone = (hexId: string): boolean => {
    const isAtZone = !!(currentHexId && hexId && currentHexId === hexId)
    return isAtZone || hasAdjacentOwnedZone(hexId)
  }

  // Get zone status for a hex
  const getZoneStatus = (hexId: string) => {
    const zone = nearbyZones.find((z) => z.hexId === hexId)
    if (zone?.zoneType === 'corrupted') return 'corrupted'
    const isOwned = zone?.ownerId === player?.id
    if (isOwned) return 'owned'
    if (zone?.ownerId) return 'enemy'
    return 'neutral'
  }

  // Get zone info for display
  const getZoneInfo = (hexId: string): { zone: Zone | null; status: string; canClaim: boolean } => {
    const zone = nearbyZones.find((z) => z.hexId === hexId) || null
    const status = getZoneStatus(hexId)
    const canClaim = status === 'neutral' && !isClaiming
    return { zone, status, canClaim }
  }

  // Per-zone rates helper — delegates to shared getZoneRates with player faction
  const zoneRates = (hexId: string, zone?: Zone) =>
    getZoneRates(hexId, { structures: zone?.structures, faction: player?.faction })

  const activeInfo = activeHexId ? getZoneInfo(activeHexId) : null
  // Always compute resources from hex (DB columns are unused)
  const activeZone = activeHexId ? playerZones.find(z => z.hexId === activeHexId) || nearbyZones.find(z => z.hexId === activeHexId) : undefined
  const activeResources = activeHexId ? zoneRates(activeHexId, activeZone) : null
  const nearbyHexes = currentHexId ? gridDisk(currentHexId, GRID_RING_SIZE) : []
  
  // Get units deployed to the active zone
  const getZoneUnits = (hexId: string) => {
    const zone = playerZones.find(z => z.hexId === hexId)
    if (!zone) return []
    return units.filter(u => u.zoneId === zone.id && u.status === 'deployed')
  }
  const activeZoneUnits = activeHexId ? getZoneUnits(activeHexId) : []

  // Get structures for a zone
  const getZoneStructures = (hexId: string) => {
    const zone = playerZones.find(z => z.hexId === hexId)
    return zone?.structures || []
  }
  const activeZoneStructures = activeHexId ? getZoneStructures(activeHexId) : []

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Map Container */}
      <div ref={mapContainer} className="w-full h-full" />

      {/* Map Overlay - Mobile First HUD */}
      <div className="absolute inset-0 pointer-events-none flex flex-col">
        
        {/* Top Bar - Minimal */}
        <div className="pointer-events-auto p-2">
          <div className="glass rounded-lg px-2 py-1.5 flex items-center justify-between text-xs">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Map className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-slate-300 font-medium">{playerStats.zonesOwned}</span>
                <span className="text-slate-500">zones</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-slate-300 font-medium">{units.filter(u => u.status === 'deployed').length}</span>
                <span className="text-slate-500">deployed</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {playerZones.length > 0 && (
                <button
                  onClick={() => {
                    const next = !showZoneNav
                    setShowZoneNav(next)
                    if (next) { fetchPlayerZones(); fetchUnits() }
                  }}
                  className={`p-1.5 rounded-md ${showZoneNav ? 'bg-emerald-500/30 text-emerald-400' : 'hover:bg-white/10'}`}
                  title="My Zones"
                >
                  <LayoutList className="w-4 h-4" />
                </button>
              )}
              <button onClick={centerOnUser} className="p-1.5 hover:bg-white/10 rounded-md" disabled={!userLocation}>
                <Locate className="w-4 h-4" />
              </button>
              <button
                onClick={toggleTracking}
                className={`p-1.5 rounded-md ${isTracking ? 'bg-cyan-500/30 text-cyan-400' : 'hover:bg-white/10'}`}
              >
                <Crosshair className="w-4 h-4" />
              </button>
              {import.meta.env.DEV && (
                <button
                  onClick={() => setDevTeleport(!devTeleport)}
                  className={`p-1.5 rounded-md ${devTeleport ? 'bg-amber-500/30 text-amber-400' : 'hover:bg-white/10 text-slate-500'}`}
                  title="Dev: Click map to teleport"
                >
                  <Navigation className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Dev Teleport Banner */}
        {import.meta.env.DEV && devTeleport && (
          <div className="pointer-events-auto px-2">
            <div className="glass rounded-lg px-3 py-1.5 flex items-center gap-2 border border-amber-500/30 bg-amber-500/10">
              <Navigation className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span className="text-[11px] text-amber-300 font-medium flex-1">DEV: Click map to teleport</span>
              <button
                onClick={devSpawnCorrupted}
                disabled={isDevSpawning || !userLocation}
                className="px-2 py-1 rounded text-[10px] font-semibold bg-purple-500/30 text-purple-300 hover:bg-purple-500/50 transition flex items-center gap-1 disabled:opacity-50"
              >
                {isDevSpawning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bug className="w-3 h-3" />}
                Spawn PvE
              </button>
            </div>
          </div>
        )}

        {/* GPS Error Banner */}
        {gpsError && (
          <div className="pointer-events-auto px-2">
            <div className="glass rounded-lg px-3 py-2 flex items-center gap-2 border border-amber-500/30 bg-amber-500/10">
              <MapPinOff className="w-4 h-4 text-amber-400 shrink-0" />
              <span className="text-xs text-amber-300">{gpsError}</span>
            </div>
          </div>
        )}

        {/* Zone Navigator Panel — vertical list, right half */}
        {showZoneNav && playerZones.length > 0 ? (
          <div className="flex-1 min-h-0 flex flex-col items-end pt-1 px-2">
            <div className="pointer-events-auto w-1/2 rounded-lg border border-slate-600/40 overflow-hidden bg-slate-800/80 backdrop-blur-sm flex flex-col flex-1 min-h-0">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-600/30 shrink-0">
                <span className="text-[10px] text-slate-300 uppercase tracking-wider font-semibold">My Zones ({playerZones.length})</span>
                <button onClick={() => setShowZoneNav(false)} className="p-0.5 hover:bg-white/10 rounded">
                  <X className="w-3 h-3 text-slate-400" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 p-1.5 space-y-1 scrollbar-thin">
                {playerZones.map((zone) => {
                  const zoneUnits = units.filter(u => u.zoneId === zone.id && u.status === 'deployed')
                  const structCount = zone.structures?.length || 0
                  const isActive = zone.hexId === activeHexId
                  const isCurrent = zone.hexId === currentHexId

                  const biome = getZoneBiome(zone.hexId)
                  const zoneName = getZoneName(zone.hexId)
                  const rates = zoneRates(zone.hexId, zone)

                  return (
                    <button
                      key={zone.id}
                      onClick={() => {
                        if (zone.hexId === selectedHexId) {
                          setSelectedHexId(null)
                        } else {
                          setSelectedHexId(zone.hexId)
                          flyToZone(zone.hexId)
                        }
                      }}
                      className={`w-full rounded-lg px-3 py-2 text-left transition-all border ${
                        isActive
                          ? 'bg-emerald-500/15 border-emerald-500/40'
                          : 'bg-slate-700/50 border-slate-600/30 hover:bg-slate-700/70 hover:border-slate-500/40'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse shrink-0" />}
                        <span className="text-[11px] font-semibold text-slate-100 truncate">{zoneName}</span>
                        <span className="text-[9px] shrink-0 ml-auto" style={{ color: biome.color }}>{biome.symbol}</span>
                        {zoneUnits.length > 0 && (
                          <span className="flex items-center gap-0.5 text-cyan-400 text-[9px] shrink-0">
                            <Shield className="w-2.5 h-2.5" />{zoneUnits.length}
                          </span>
                        )}
                        {structCount > 0 && (
                          <span className="flex items-center gap-0.5 text-violet-400 text-[9px] shrink-0">
                            <Building2 className="w-2.5 h-2.5" />{structCount}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-[9px] mt-1">
                        {RESOURCE_ORDER.map(k => {
                          const val = rates[k]
                          if (k === 'darkMatter' && val < 0.1) return null
                          return (
                            <span key={k} className={`${RESOURCE_CONFIG[k].color} font-medium`}>
                              {RESOURCE_CONFIG[k].symbol}{val}
                            </span>
                          )
                        })}
                        <span className="text-slate-500 text-[8px]">/h</span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Spacer when zone nav is closed */
          <div className="flex-1" />
        )}

        {/* Bottom Panel - Zone Info (above bottom nav) */}
        <div className="pointer-events-auto p-2 pb-16 space-y-2">

          {/* Main Zone Card - Compact for Mobile */}
          <div className="glass rounded-xl overflow-hidden">
            {/* Header Row: Status + Zone Name + Counts + Action */}
            <div className="px-3 py-2.5 flex items-center gap-2">
              {/* Status indicator + expand toggle */}
              <button
                onClick={() => setShowZoneDetails(!showZoneDetails)}
                className="flex items-center gap-1 shrink-0"
              >
                <div className={`w-2.5 h-2.5 rounded-full ${
                  activeInfo?.status === 'corrupted' ? 'bg-purple-400 animate-pulse' :
                  activeInfo?.status === 'owned' ? 'bg-emerald-400' :
                  activeInfo?.status === 'enemy' ? 'bg-amber-400' :
                  'bg-cyan-400'
                }`} />
                {showZoneDetails ? <ChevronDown className="w-3.5 h-3.5 text-slate-300" /> : <ChevronUp className="w-3.5 h-3.5 text-slate-300" />}
              </button>

              {/* Zone name + biome */}
              {activeHexId && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-xs font-semibold text-slate-200 truncate">{getZoneName(activeHexId)}</span>
                  <span className="text-[10px] shrink-0" style={{ color: getZoneBiome(activeHexId).color }}>{getZoneBiome(activeHexId).symbol}</span>
                </div>
              )}

              <div className="flex-1" />

              {/* Inline counts when collapsed */}
              {!showZoneDetails && (activeZoneUnits.length > 0 || activeZoneStructures.length > 0) && (
                <div className="flex items-center gap-1.5 shrink-0 text-slate-400">
                  {activeZoneUnits.length > 0 && (
                    <span className="flex items-center gap-0.5 text-[10px]">
                      <Shield className="w-3 h-3" />{activeZoneUnits.length}
                    </span>
                  )}
                  {activeZoneStructures.length > 0 && (
                    <span className="flex items-center gap-0.5 text-[10px]">
                      <Building2 className="w-3 h-3" />{activeZoneStructures.length}
                    </span>
                  )}
                </div>
              )}

              {/* Action Button */}
              <div className="shrink-0">
                {claimError ? (
                  <span className="text-xs text-red-400">{claimError}</span>
                ) : activeInfo?.status === 'owned' ? (
                  <div className="flex items-center gap-1">
                    {idleUnits.length > 0 && activeZoneUnits.length === 0 && (
                      <button
                        onClick={() => setShowQuickDeploy(!showQuickDeploy)}
                        disabled={isDeploying}
                        className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition flex items-center gap-1"
                      >
                        {isDeploying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        Deploy
                      </button>
                    )}
                    {activeZoneUnits.length > 0 && (
                      <button
                        onClick={handleQuickRecall}
                        disabled={isDeploying}
                        className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-white/5 text-slate-300 hover:bg-white/10 transition flex items-center gap-1"
                      >
                        {isDeploying ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowLeftCircle className="w-3 h-3" />}
                        Recall
                      </button>
                    )}
                    {idleUnits.length === 0 && activeZoneUnits.length === 0 && (
                      <span className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-emerald-500/15 text-emerald-400">
                        Owned
                      </span>
                    )}
                  </div>
                ) : activeInfo?.status === 'corrupted' ? (
                  activeHexId && canReachZone(activeHexId) ? (
                    <button
                      onClick={() => {
                        const next = !showPveSelect
                        setShowPveSelect(next)
                        if (next) {
                          const zone = nearbyZones.find(z => z.hexId === activeHexId)
                          if (zone) fetchSpawnInfo(zone.id)
                        } else {
                          setPveSpawnInfo(null)
                        }
                      }}
                      className="px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all bg-purple-500/80 hover:bg-purple-500 text-white flex items-center gap-1"
                    >
                      <Skull className="w-3 h-3" />
                      Purge
                    </button>
                  ) : (
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-slate-700/50 text-slate-500">
                      Move here to purge
                    </span>
                  )
                ) : activeInfo?.status === 'enemy' ? (
                  activeHexId && getZoneModifiers(activeHexId).some(m => m.id === 'sanctuary') ? (
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
                      🕊 Sanctuary
                    </span>
                  ) : activeHexId && canReachZone(activeHexId) ? (
                    <button
                      onClick={() => activeHexId && handleAttackZone(activeHexId)}
                      className="px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all bg-red-500/80 hover:bg-red-500 text-white"
                    >
                      Attack
                    </button>
                  ) : (
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-slate-700/50 text-slate-500">
                      Move here to attack
                    </span>
                  )
                ) : (
                  <button
                    onClick={() => {
                      if (selectedHexId && selectedHexId !== currentHexId) {
                        setClaimError('Move here first')
                        setTimeout(() => setClaimError(null), 2000)
                        return
                      }
                      handleClaimZone()
                    }}
                    disabled={!activeInfo?.canClaim || !!(selectedHexId && selectedHexId !== currentHexId)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                      activeInfo?.canClaim && (!selectedHexId || selectedHexId === currentHexId)
                        ? 'bg-cyan-500 text-white'
                        : 'bg-slate-700/50 text-slate-500'
                    }`}
                  >
                    {isClaiming ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : 'Claim'}
                  </button>
                )}
              </div>
            </div>

            {/* Expandable Details */}
            {showZoneDetails && activeHexId && (
              <div className="border-t border-white/[0.06] px-3 py-2 space-y-2">
                {/* Biome + Modifiers row */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-300" style={{ borderLeft: `2px solid ${getZoneBiome(activeHexId).color}` }}>
                    {getZoneBiome(activeHexId).symbol} {getZoneBiome(activeHexId).name}
                  </span>
                  {getZoneModifiers(activeHexId).map(m => (
                    <span
                      key={m.id}
                      className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        m.id === 'sanctuary' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' :
                        m.id === 'rich_deposit' ? 'bg-emerald-500/15 text-emerald-300' :
                        m.id === 'volatile' ? 'bg-red-500/15 text-red-300' :
                        m.id === 'radiation' ? 'bg-orange-500/15 text-orange-300' :
                        'bg-white/[0.06] text-slate-300'
                      }`}
                    >
                      {m.symbol} {m.name}
                    </span>
                  ))}
                </div>

                {/* Zone Resources */}
                {activeResources && (
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-widest font-medium mb-1">Production /h</p>
                    <div className="flex items-center gap-2.5">
                      {RESOURCE_ORDER.map(k => (
                        <span key={k} className={`text-[11px] font-medium ${RESOURCE_CONFIG[k].color}`}>
                          {RESOURCE_CONFIG[k].symbol}+{activeResources[k]}
                        </span>
                      ))}
                    </div>
                    {activeZone?.structures?.some(s => s.structureType === 'mining_rig' && s.active) && (
                      <p className="text-[9px] text-emerald-400/60 mt-0.5">Boosted by Mining Rig</p>
                    )}
                  </div>
                )}

                {/* Deployed Units */}
                {activeZoneUnits.length > 0 && (
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-widest font-medium mb-1">Deployed</p>
                    <div className="flex items-center gap-1.5 overflow-x-auto">
                      {activeZoneUnits.map(u => (
                        <div key={u.id} className="flex items-center gap-1.5 py-1 px-1.5 rounded-md bg-white/[0.04] border border-white/[0.06] shrink-0">
                          <UnitSprite unitType={u.unitType} level={u.level} status={u.status} size="sm" showLevel={true} animate={false} />
                          <div className="text-[10px] pr-1">
                            <p className="text-slate-200 font-medium leading-tight">{UNIT_META[u.unitType].shortName}</p>
                            <p className="text-slate-400 leading-tight">Lv.{u.level}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Structures */}
                {activeZoneStructures.length > 0 && (
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-widest font-medium mb-1">Structures</p>
                    <div className="flex items-center gap-1.5 overflow-x-auto">
                      {activeZoneStructures.map(s => (
                        <div key={s.id} className="flex items-center gap-1.5 py-1 px-1.5 rounded-md bg-white/[0.04] border border-white/[0.06] shrink-0">
                          <StructureSprite structureType={s.structureType} level={s.level} size="sm" showLevel={true} />
                          <div className="text-[10px] pr-1">
                            <p className={`font-medium leading-tight ${STRUCTURE_META[s.structureType].color}`}>{STRUCTURE_META[s.structureType].shortName}</p>
                            <p className="text-slate-400 leading-tight">Lv.{s.level}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quick Deploy Unit Picker */}
            {showQuickDeploy && activeInfo?.status === 'owned' && idleUnits.length > 0 && (
              <div className="border-t border-white/10 p-2">
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {idleUnits.slice(0, 5).map((unit) => (
                    <button
                      key={unit.id}
                      onClick={() => handleQuickDeploy(unit.id)}
                      disabled={isDeploying}
                      className="flex items-center justify-center p-1 rounded-lg bg-white/5 hover:bg-white/10 transition shrink-0"
                    >
                      <UnitSprite
                        unitType={unit.unitType}
                        level={unit.level}
                        status="idle"
                        size="sm"
                        showLevel={true}
                        animate={false}
                      />
                    </button>
                  ))}
                  {idleUnits.length > 5 && (
                    <span className="text-[10px] text-slate-500 shrink-0">+{idleUnits.length - 5} more</span>
                  )}
                </div>
              </div>
            )}

            {/* PvE Unit Picker */}
            {showPveSelect && activeInfo?.status === 'corrupted' && (
              <div className="border-t border-purple-500/30 p-3 bg-purple-500/5">
                {/* Enemy Intel */}
                {pveSpawnInfo && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px] text-red-400 uppercase tracking-wider font-semibold">Hostile Units</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-semibold">
                        Difficulty {pveSpawnInfo.difficulty}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 overflow-x-auto pb-1">
                      {pveSpawnInfo.aiUnits.map((aiUnit, i) => (
                        <div key={i} className="flex items-center gap-1.5 p-1.5 rounded-lg bg-red-500/10 border border-red-500/20 shrink-0">
                          <UnitSprite
                            unitType={aiUnit.unitType}
                            level={aiUnit.level}
                            status="deployed"
                            size="sm"
                            showLevel={true}
                            animate={false}
                            corrupted
                          />
                          <div className="text-[10px]">
                            <p className="text-red-300 font-medium">{UNIT_META[aiUnit.unitType].shortName}</p>
                            <p className="text-slate-500">Lv.{aiUnit.level}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-[10px] text-purple-400 uppercase tracking-wider mb-2">Select units to purge (max 3)</p>
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {idleUnits.map((unit) => {
                    const selected = pveSelectedUnits.includes(unit.id)
                    return (
                      <button
                        key={unit.id}
                        onClick={() => togglePveUnit(unit.id)}
                        className={`flex items-center justify-center p-1 rounded-lg transition shrink-0 border ${
                          selected ? 'border-purple-400 bg-purple-500/20' : 'border-transparent bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <UnitSprite
                          unitType={unit.unitType}
                          level={unit.level}
                          status="idle"
                          size="sm"
                          showLevel={true}
                          animate={false}
                        />
                      </button>
                    )
                  })}
                </div>
                {pveSelectedUnits.length > 0 && (
                  <button
                    onClick={() => {
                      const zone = nearbyZones.find(z => z.hexId === activeHexId)
                      if (zone) handlePveAttack(zone.id)
                    }}
                    disabled={isPveAttacking}
                    className="mt-2 w-full py-2 rounded-lg text-xs font-semibold bg-purple-500 hover:bg-purple-400 text-white transition flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    {isPveAttacking ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Skull className="w-3.5 h-3.5" />
                        Attack with {pveSelectedUnits.length} unit{pveSelectedUnits.length > 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Claim Success Flash */}
      {claimFlash && (
        <div className="absolute inset-0 pointer-events-none z-40">
          <div className="absolute inset-0 bg-emerald-400/10 animate-fade-in" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 animate-slide-up-fade">
            <span className="text-emerald-400 font-display font-bold text-lg text-glow-emerald whitespace-nowrap">
              ZONE CLAIMED
            </span>
          </div>
        </div>
      )}

      {/* PvE Result Overlay */}
      {pveResult && (
        <BattleResultOverlay
          victory={pveResult.victory}
          turns={pveResult.turns}
          battleType="pve"
          playerUnits={pveResult.playerUnits}
          enemyUnits={pveResult.aiUnits}
          highlights={pveResult.highlights || []}
          rewards={pveResult.rewards}
          modDrop={pveResult.modDrop}
          onContinue={() => setPveResult(null)}
        />
      )}

      {/* Crosshair in center */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        {/* Outer rotating dashed ring */}
        <div className="absolute -inset-3 w-12 h-12 rounded-full crosshair-ring" />
        {/* Static ring with tick marks */}
        <div className="relative w-6 h-6 border-2 border-cyan-400/60 rounded-full flex items-center justify-center">
          {/* Tick marks - N/S/E/W */}
          <div className="absolute w-[2px] h-1.5 bg-cyan-400/70 -top-0.5 left-1/2 -translate-x-1/2" />
          <div className="absolute w-[2px] h-1.5 bg-cyan-400/70 -bottom-0.5 left-1/2 -translate-x-1/2" />
          <div className="absolute h-[2px] w-1.5 bg-cyan-400/70 top-1/2 -left-0.5 -translate-y-1/2" />
          <div className="absolute h-[2px] w-1.5 bg-cyan-400/70 top-1/2 -right-0.5 -translate-y-1/2" />
          {/* Center dot */}
          <div className="w-1 h-1 bg-cyan-400 rounded-full animate-pulse" />
        </div>
      </div>

      {/* Battle Modal Overlay */}
      {showBattle && battleZoneId && (
        <div className="absolute inset-0 z-50 bg-slate-950/95 backdrop-blur-sm animate-fade-in">
          <div className="absolute top-2 right-2 z-10">
            <button
              onClick={closeBattle}
              className="p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <BattlePanel zoneId={battleZoneId} onClose={closeBattle} />
        </div>
      )}
    </div>
  )
}
